/**
 * LeadLoop — the one feed for "who needs contact next".
 *
 * Driven by each lead's own return date (next_follow_up_at): due and overdue
 * leads surface here, future-dated leads sleep, and open leads with nothing
 * scheduled show up as "Needs next step" so nothing ever falls through the
 * cracks. Work it top to bottom, record what happened, set the next step.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  ArrowRight,
  Building2,
  Mail,
  Pencil,
  Repeat,
  Sparkles,
  StickyNote,
  User,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { lccApi, type LccApproval, type LccLead, type LccLoopFilter } from '@/lib/lcc-api';
import {
  ApprovalPendingBadge,
  ApprovalRegenerateDialog,
  ApprovalReviewDialog,
} from './approval-actions';
import {
  formatLccLabel,
  formatLccRelative,
  LccDueBadge,
  LccErrorCard,
  LccLoadingCard,
  LccPageHeader,
  LccStatusBadge,
  nextActionForLead,
} from './lcc-shared';
import { CallButton, EmailComposeDialog, QuickAssignSelect, useLccInvalidate } from './lead-actions';
import { RecordInteractionDialog, VerifiedCheck } from './record-interaction';

const FILTERS: Array<{ value: LccLoopFilter; label: string }> = [
  { value: 'all', label: 'Everything' },
  { value: 'due', label: 'Due' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'approvals', label: 'Approvals' },
  { value: 'new', label: 'New' },
  { value: 'unverified', label: 'Unverified' },
  { value: 'unassigned', label: 'Unassigned' },
];

export function LeadLoopView() {
  const [filter, setFilter] = useState<LccLoopFilter>('all');
  const { data, isLoading, error } = useQuery({
    queryKey: ['lcc', 'loop', filter],
    queryFn: () => lccApi.loop(filter),
  });

  return (
    <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-4">
      <LccPageHeader
        title="LeadLoop"
        blurb="Everyone who needs contact next, most urgent first. Work it top to bottom: record what happened, set the next step, and the lead comes back when it's due. If the loop is clear, nothing is falling through the cracks."
      />

      <div className="flex flex-wrap gap-1.5">
        {FILTERS.map((f) => (
          <Button
            key={f.value}
            type="button"
            size="sm"
            variant={filter === f.value ? 'default' : 'outline'}
            className="h-7 rounded-full px-3 text-[12px]"
            onClick={() => setFilter(f.value)}
          >
            {f.label}
          </Button>
        ))}
      </div>

      {isLoading && <LccLoadingCard />}
      {error != null && <LccErrorCard title="Couldn't load the LeadLoop" error={error} />}
      {!isLoading && error == null && (
        <div className="flex flex-col gap-2.5">
          {(data?.items ?? []).length === 0 && (
            <Card>
              <div className="flex flex-col items-center gap-2 px-4 py-12 text-center text-sm text-muted-foreground">
                <Repeat className="h-6 w-6 text-muted-foreground/50" aria-hidden />
                <span>Loop clear — every lead has a scheduled next step.</span>
              </div>
            </Card>
          )}
          {(data?.items ?? []).map((item) => (
            <LeadLoopRow key={item.lead.id} lead={item.lead} approval={item.approval} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * The lead card. While an approval is pending the manual action row is
 * replaced by Review & Edit / Regenerate / Cancel Action; cancelling archives
 * the approval and the card falls back to the manual actions.
 */
export function LeadLoopRow({ lead, approval }: { lead: LccLead; approval?: LccApproval }) {
  const navigate = useNavigate();
  const invalidate = useLccInvalidate();
  const [emailOpen, setEmailOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [regenerateOpen, setRegenerateOpen] = useState(false);
  const detailPath = `/demo/lcc/leads/${lead.id}`;

  const cancelApproval = useMutation({
    mutationFn: (id: string) => lccApi.cancelApproval(id),
    onSuccess: invalidate,
  });

  return (
    <Card className="p-3.5 transition-colors hover:border-primary/40">
      <div className="flex flex-col gap-2.5">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <VerifiedCheck lead={lead} />
              <Link to={detailPath} className="text-[14px] font-semibold leading-tight underline-offset-2 hover:underline">
                {lead.title}
              </Link>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-muted-foreground">
              {lead.contactName && (
                <span className="inline-flex items-center gap-1">
                  <User className="h-3 w-3" aria-hidden />
                  {lead.contactName}
                </span>
              )}
              {(lead.organization?.name ?? lead.companyName) && (
                <span className="inline-flex items-center gap-1">
                  <Building2 className="h-3 w-3" aria-hidden />
                  {lead.organization?.name ?? lead.companyName}
                </span>
              )}
              <span>{formatLccLabel(lead.source)}</span>
              <span title={lead.createdAt}>{formatLccRelative(lead.createdAt)}</span>
              {lead.phone && <span className="tabular-nums">{lead.phone}</span>}
              {lead.email && <span className="break-all">{lead.email}</span>}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            {approval && <ApprovalPendingBadge />}
            <LccDueBadge lead={lead} />
            <LccStatusBadge status={lead.status} label={lead.statusLabel} />
          </div>
        </div>

        {approval ? (
          <div className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-[12.5px] leading-snug">
            <span className="font-medium">Proposed action:</span> {approval.title}
            {approval.description && (
              <span className="text-muted-foreground"> — {approval.description}</span>
            )}
          </div>
        ) : (
          (lead.aiSummary ?? lead.message) && (
            <p className="line-clamp-2 max-w-[90ch] text-[12.5px] leading-snug text-muted-foreground">
              {lead.aiSummary ?? lead.message}
            </p>
          )
        )}

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border/60 pt-2">
          <span className="inline-flex items-center gap-1.5 text-[12px] font-medium text-primary">
            <ArrowRight className="h-3.5 w-3.5" aria-hidden />
            {approval ? 'Review the proposed action' : nextActionForLead(lead)}
          </span>
          {approval ? (
            <div className="flex flex-wrap items-center gap-1.5">
              <Button type="button" size="sm" className="gap-1.5" onClick={() => setReviewOpen(true)}>
                <Pencil className="h-3.5 w-3.5" aria-hidden />
                Review & Edit
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="gap-1.5"
                onClick={() => setRegenerateOpen(true)}
              >
                <Sparkles className="h-3.5 w-3.5" aria-hidden />
                Regenerate
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="gap-1.5"
                disabled={cancelApproval.isPending}
                onClick={() => cancelApproval.mutate(approval.id)}
              >
                <X className="h-3.5 w-3.5" aria-hidden />
                Cancel Action
              </Button>
              <Button type="button" size="sm" variant="outline" className="gap-1.5" onClick={() => navigate(detailPath)}>
                Open
                <ArrowRight className="h-3.5 w-3.5" aria-hidden />
              </Button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-1.5">
              <CallButton lead={lead} />
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="gap-1.5"
                disabled={!lead.email}
                title={lead.email ? undefined : 'No email on file'}
                onClick={() => setEmailOpen(true)}
              >
                <Mail className="h-3.5 w-3.5" aria-hidden />
                Email
              </Button>
              <Button type="button" size="sm" variant="outline" className="gap-1.5" onClick={() => setNoteOpen(true)}>
                <StickyNote className="h-3.5 w-3.5" aria-hidden />
                Note
              </Button>
              <QuickAssignSelect lead={lead} />
              <Button type="button" size="sm" className="gap-1.5" onClick={() => navigate(detailPath)}>
                Open
                <ArrowRight className="h-3.5 w-3.5" aria-hidden />
              </Button>
            </div>
          )}
        </div>
      </div>

      <EmailComposeDialog lead={lead} open={emailOpen} onOpenChange={setEmailOpen} />
      <RecordInteractionDialog
        lead={lead}
        open={noteOpen}
        onOpenChange={setNoteOpen}
        defaults={{ interactionType: 'internal_note' }}
      />
      {approval && (
        <>
          <ApprovalReviewDialog approval={approval} open={reviewOpen} onOpenChange={setReviewOpen} />
          <ApprovalRegenerateDialog
            approval={approval}
            open={regenerateOpen}
            onOpenChange={setRegenerateOpen}
          />
        </>
      )}
    </Card>
  );
}
